import Papa from 'papaparse';
import * as R from 'ramda';
import { artificialWait } from './async';
import { getTokens } from './auth/auth-client';
import { NotSignedInError } from './auth/auth-errors';
// From ./auth/session, not ./auth/auth — auth.ts imports UsersLambdaFetch/awaitJSON from this
// file, so importing back from auth.ts here would create a cycle. session.ts holds
// captureIntendedDestination/loginRedirect (and their dependencies) with no fetch-utils dependency.
import { captureIntendedDestination, loginRedirect } from './auth/session';
import { download } from './download-utils';
import { reportError } from './errorReporting';
import { FetchError, findFetchError } from './fetch-error';
import { Mixpanel, MxE } from './mixpanelWrapper';
import { markHostReachable } from './unreachable-hosts';
import { UntraceableError } from './untraceable-error';

// @ts-ignore
const REACT_APP_LAMBDA_HOST = import.meta.env.VITE_LAMBDA_HOST || '';
// @ts-ignore
const REACT_APP_DOCKER_LAMBDA_HOST = import.meta.env.VITE_DOCKER_LAMBDA_HOST || '';
// @ts-ignore
const REACT_APP_LOCAL_PYTHON_HOST = import.meta.env.VITE_LOCAL_PYTHON_HOST || '';

const S3_POLLING_PERIOD = 1000;
const S3_POLLING_TIMEOUT = 5 * 60;
// A Comet worker Lambda can run up to 15 minutes.
export const COMET_WORKER_TIMEOUT_SECONDS = 15 * 60;
// A worker that dies hard (e.g. Runtime.OutOfMemory) writes no result file, so `noTimeout` polling
// would never end. Comet polling therefore still stops at the Lambda max runtime plus a margin
// (DEV-26064); a live worker can never legitimately run longer than that.
export const COMET_NO_TIMEOUT_CEILING_SECONDS = COMET_WORKER_TIMEOUT_SECONDS + 5 * 60;

export const getCleanEmail = (email: string): string | null => {
  let tempEmail = email;
  // remove all whitespace
  tempEmail = tempEmail.replace(/\s/g, '');
  // https://stackoverflow.com/questions/46155/how-to-validate-an-email-address-in-javascript
  const match = tempEmail.match(
    /^(([^<>()[\].,;:\s@"]+(\.[^<>()[\].,;:\s@"]+)*)|(".+"))@(([^<>()[\].,;:\s@"]+\.)+[^<>()[\].,;:\s@"]{2,})$/i
  );
  return match && match[0];
};

export const awaitText = (response: Response): Promise<string> => response.text();

// Mirrors bpm-lambdas' lib/cometWrapper.ts's CometAckResponse. Not imported from
// @blisspointmedia/bpm-types -- that type isn't published there yet (deferred until a caller needs
// it); defined locally here for the same reason, to avoid a cross-repo publish for one shape.
interface CometAckResponse {
  comet: true;
  uuid: string;
}

const isCometAckResponse = (value: unknown): value is CometAckResponse =>
  typeof value === 'object' &&
  value !== null &&
  (value as CometAckResponse).comet === true &&
  typeof (value as CometAckResponse).uuid === 'string';

// Accepts anything with a `.text()` method -- both `Response` and the `Blob` that `pollS3`
// resolves to satisfy this, so callers polling S3 for JSON get the same parse-failure
// diagnostics (console log + descriptive error) as a direct lambda fetch.
export const awaitJSON = async <T = any>(response: { text(): Promise<string> }): Promise<T> => {
  const text = await response.text();
  let json: unknown;
  try {
    json = JSON.parse(text);
  } catch {
    const msg = `Failed to parse response as JSON. Parsed as text:\n${text}`;
    console.error(msg);
    throw new UntraceableError(msg, 'gateway');
  }
  // DEV-24669: a handler composed with bpm-lambdas' Comet self-invoke wrapper pair (DEV-24668,
  // lib/cometWrapper.ts) acks {comet:true, uuid} instead of running synchronously. Detecting that
  // shape here -- the one place a Response becomes typed data for every awaitJSON call site -- means
  // any handler that adopts the wrapper becomes Comet-capable for free: the caller just sets
  // __cometTrigger on its request and keeps calling awaitJSON exactly as before, no per-call-site
  // polling logic needed. pollCometResult is defined further down this file (with the rest of the
  // Comet polling machinery); referencing it here is fine since this only resolves when awaitJSON is
  // actually called, after the whole module has finished initializing.
  if (isCometAckResponse(json)) {
    return pollCometResult<T>(json.uuid, { ext: 'json' });
  }
  return json as T;
};

const getErrorMessage = (error: unknown): string =>
  error instanceof Error ? error.message : String(error);

// Safe to interpolate directly into a user-facing message: returns the error's own message when
// there is one, otherwise a generic fallback. Unlike naive `${error}` interpolation or
// getErrorMessage's String(error) fallback (fine for telemetry, not for the UI), this never
// renders a non-Error throw (a plain object, an array, etc.) as the meaningless "[object Object]".
export const getSafeErrorMessage = (error: unknown, fallback = 'an unknown error'): string => {
  if (typeof error === 'string') {
    return error;
  }
  if (error instanceof Error) {
    return error.message;
  }
  return fallback;
};

// Message safe to show for an arbitrary caught error: a backend or transport message when the error
// is (or wraps) one, otherwise the fallback so a raw JS error text never reaches the dialog.
export const getUserFacingMessage = (error: unknown, fallback: string): string => {
  if (error instanceof UntraceableError) return error.message;
  return findFetchError(error)?.message ?? fallback;
};

// Bucket a rejected fetch() into a low-cardinality type so the telemetry stays queryable.
// A rejected fetch() is always transport-level (network/CORS/abort/timeout), never an HTTP status.
const classifyFetchError = (error: unknown): string => {
  const msg = getErrorMessage(error).toLowerCase();
  if (msg.includes('abort')) {
    return 'aborted';
  }
  if (msg.includes('timeout') || msg.includes('timed out')) {
    return 'timeout';
  }
  if (msg.includes('failed to fetch') || msg.includes('networkerror') || msg.includes('network')) {
    return 'network';
  }
  return 'other';
};

// User-facing text per classifyFetchError bucket — plain English, never the request's own
// url/params, since (unlike an HTTP error body) a transport-level failure has no backend
// message to show and previously leaked a raw {message, path, params} JSON blob here instead.
const TRANSPORT_ERROR_MESSAGES: Record<string, string> = {
  timeout: 'This request timed out. Please try again in a moment.',
  network: "We couldn't reach the server. Check your connection and try again.",
  aborted: 'The request was interrupted. Please try again.',
  other: 'Something went wrong completing this request. Please try again.'
};

// Snapshot of the client's transport state at failure time. Lets us split genuine
// offline/backgrounded give-ups (expected, client-side) from online+visible failures
// (which point at a server or CORS-masked error worth backend attention).
const getFailureContext = (): {
  online: boolean;
  visibility: string;
  connection?: string;
  page: string;
} => {
  const nav = navigator as Navigator & { connection?: { effectiveType?: string } };
  return {
    online: navigator.onLine,
    visibility: document.visibilityState,
    connection: nav.connection?.effectiveType,
    page: window.location.pathname
  };
};

const isTabHidden = (): boolean => document.visibilityState === 'hidden';

// Default bound for how long to wait for a hidden tab to come back to the foreground before
// giving up on it and letting the caller proceed anyway. Long, because the default case is an
// independent, unattended fetch (a page's per-widget data load on mount) where nobody is watching
// a spinner on a hidden tab — waiting longer before the final give-up costs nothing and covers the
// common "opened this page, went to go do something else for a few minutes" pattern (confirmed via
// lambda_fetch_failed telemetry: pages like SlideGenerator and Dashboard chart-view showed
// near-100% of their failures with a bonus attempt that still lost to a too-short bound). See
// LambdaFetchOptions.visibilityWaitTimeoutMs for the one case that needs a shorter bound instead.
export const DEFAULT_VISIBILITY_WAIT_TIMEOUT_MS = 10 * 60 * 1000;

// Short override for a caller that chains requests sequentially — each item's fetch must complete
// before the next one starts (a batch job awaiting each item in turn, e.g. via fetchWithRetry) —
// where the long default would compound across N items and stall the whole sequence on one
// permanently-backgrounded tab, instead of just delaying one independent fetch. Pass this
// explicitly via visibilityWaitTimeoutMs. Confirmed sequential consumers as of 2026-09-23:
// CommerceScraper's batch-scrape loop, StreamingBuying's sequential order-import loops, and
// SegmentationIdCreation's per-row ID-generation loop.
export const SEQUENTIAL_VISIBILITY_WAIT_TIMEOUT_MS = 2 * 60 * 1000;

// BpmLambdaFetch's give-up path (its only caller) reaches this once every scheduled retry has
// failed and the tab was hidden at some point along the way. Resolves immediately if the tab is
// already visible; otherwise waits for it to come back to the foreground — or for timeoutMs to
// elapse, whichever comes first — before giving the caller one last attempt. Always resolves
// (never rejects), so that attempt runs and fails or succeeds normally rather than needing
// separate timeout-handling.
const waitForVisible = (timeoutMs: number): Promise<void> => {
  if (!isTabHidden()) {
    return Promise.resolve();
  }
  return new Promise<void>(resolve => {
    let timeoutId: ReturnType<typeof setTimeout>;
    const cleanup = () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      clearTimeout(timeoutId);
    };
    const onVisibilityChange = () => {
      if (!isTabHidden()) {
        cleanup();
        resolve();
      }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    timeoutId = setTimeout(() => {
      cleanup();
      resolve();
    }, timeoutMs);
  });
};

export const awaitStatus = async (response: Response): Promise<boolean> => {
  // If there is no status, that means the fetch was canceled, and we shouldn't do anything
  if (!response.status) {
    return true;
  }
  if (response.status >= 200 && response.status < 300) {
    // 300s might be needed as well (304 not modified)
    return true;
  }
  if (`${response.status}` === '503') {
    // The body may be a lambda's own error (code/requestId) or a gateway's non-JSON page.
    const body = await response.json().catch(() => undefined);
    throw new FetchError(
      'Service Unavailable',
      response.status,
      typeof body?.code === 'string' ? body.code : undefined,
      typeof body?.requestId === 'string' ? body.requestId : undefined
    );
  }
  try {
    const res = await response.json();
    const { message, code, requestId } = res;
    if (message) {
      // error.message is shown to the user as-is (see redux/modals.ts) — keep it to the
      // backend's own message, not JSON-wrapped debug context (that's for logs, not the UI).
      // A backend response is JSON, not guaranteed to match its own contract though: if `message`
      // ever comes through as something other than a string (e.g. a nested Postgres error-detail
      // object), FetchError's Error base class would silently coerce it via ToString, rendering
      // the useless "[object Object]" in the dialog instead of failing loudly or falling back.
      throw new FetchError(
        typeof message === 'string' ? message : 'The server returned an unexpected error.',
        response.status,
        typeof code === 'string' ? code : undefined,
        typeof requestId === 'string' ? requestId : undefined
      );
    }
    throw new Error();
  } catch (e) {
    console.error(e);
    // Bodyless/non-JSON 404s (e.g. API Gateway's own "no such route", rather than a Lambda's
    // own error() response) still fall back to a generic message here.
    throw new FetchError(e.message || `${response.status}`, response.status, e.code, e.requestId);
  }
};

// `retries` bounds attempts; `backoffMultiplier` grows the delay exponentially.
const RETRY_BACKOFF_MS = 1000;

interface FetchWithRetryOptions {
  retries?: number;
  backoffMs?: number;
  backoffMultiplier?: number;
}

export const fetchWithRetry = async <T>(
  fetchFn: () => Promise<T>,
  { retries = 1, backoffMs = RETRY_BACKOFF_MS, backoffMultiplier = 1 }: FetchWithRetryOptions = {}
): Promise<T> => {
  const attemptOnce = async (attempt: number): Promise<T> => {
    try {
      return await fetchFn();
    } catch (e) {
      if (attempt >= retries) {
        throw e;
      }
      await artificialWait(backoffMs * backoffMultiplier ** attempt);
      return attemptOnce(attempt + 1);
    }
  };
  return attemptOnce(0);
};

type ValidParam = string | number | boolean;

interface LambdaFetchOptions<T = Record<string, ValidParam | ValidParam[]>> {
  method: 'GET' | 'get' | 'POST' | 'post' | 'PUT' | 'put' | 'DELETE' | 'delete' | 'PATCH' | 'patch';
  authenticate?: boolean;
  retries?: number;
  headers: Record<string, any>;
  params?: T;
  body?: T;
  mode?: 'cors' | 'no-cors' | 'same-origin';
  devAllowDuplicate?: boolean;
  // Opt out of the recoverable-auth-error handling below (DEV-24818), for a call whose status
  // codes don't describe the caller's own session. Two known cases:
  //  - useIsAuthed's own /me boot-check call (redux/user.ts) already implements its own
  //    refresh+retry+redirect flow. Matching by endpoint (service 'users', path '/me') instead
  //    of this flag would also wrongly disable recovery for other, unrelated /me callers (e.g.
  //    CreditMemos/use-credit-memos.ts).
  //  - checkDataConnectorAuth (use-data-connector-tool.ts) proxies a THIRD-PARTY connector's own
  //    credential-check status (e.g. "is this Facebook Ads account still authorized") — its 401
  //    means the connector's external credentials are invalid, not that the caller's session is.
  skipAuthRecovery?: boolean;
  // Overrides the bonus-attempt visibility wait's default bound (DEFAULT_VISIBILITY_WAIT_TIMEOUT_MS,
  // 10 minutes — see that constant's comment). Pass SEQUENTIAL_VISIBILITY_WAIT_TIMEOUT_MS (2 min)
  // for a caller that chains requests sequentially — a batch job awaiting each item's fetch before
  // starting the next one — where the long default would compound across N items and stall the
  // whole sequence on one permanently-backgrounded tab. Every other case (independent one-shot
  // fetches, including user-triggered actions — a single click doesn't compound) is fine with the
  // long default: the wait only ever engages once the tab was already hidden during the normal
  // retries, so nobody is watching a spinner on it either way.
  visibilityWaitTimeoutMs?: number;
}

const DEFAULT_RETRIES = 2;

// Longest request URL we send.
const MAX_URL_LENGTH = 8000;
const URL_TOO_LONG_MESSAGE =
  'This request is too large to send. Narrow your selection and try again.';

// Backoff between BpmLambdaFetch's own scheduled retries (distinct from fetchWithRetry's
// caller-configurable backoff above, and from the visibility-wait bonus attempt below) —
// exponential with full jitter instead of a flat wait every time. This gives a truly momentary
// blip a chance to clear well before the old fixed 5s, and — since a page that fires many
// independent requests at once (one per widget/card) previously had every one of them retrying in
// lockstep at the exact same instant — spreads them out instead of retrying as a synchronized
// burst. attemptNumber is 1-based (the attempt that just failed).
const RETRY_BACKOFF_BASE_MS = 1000;
const RETRY_BACKOFF_MAX_MS = 8000;
const jitteredRetryBackoffMs = (attemptNumber: number): number => {
  const exponential = Math.min(
    RETRY_BACKOFF_BASE_MS * 2 ** (attemptNumber - 1),
    RETRY_BACKOFF_MAX_MS
  );
  // Full jitter within the top half of the exponential window, so later retries still trend
  // longer on average without ever being shorter than half of what a plain exponential would give.
  return exponential / 2 + Math.random() * (exponential / 2);
};

// Dev-only duplicate request tracking. Vite tree-shakes all references when import.meta.env.DEV is false.
const inFlight = new Map<string, Error>();

const buildFingerprint = (
  method: string,
  service: string,
  path: string,
  params: unknown,
  body: string | undefined
): string => {
  const sortedParams = params
    ? Object.entries(params as Record<string, unknown>)
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([k, v]) => `${k}=${JSON.stringify(v)}`)
        .join('&')
    : '';
  return `${method.toUpperCase()}:${service}${path}?${sortedParams}|${body ?? ''}`;
};

// Builds the Authorization header value from the current access token. Used both before the
// retry loop and again before the bonus attempt (whose token may have gone stale during an
// arbitrarily long visibility wait), so the two call sites can't drift out of sync.
const buildAuthHeader = async (): Promise<string> => {
  try {
    const { accessToken } = await getTokens();
    return `bearer ${accessToken}`;
  } catch (e) {
    if (e instanceof NotSignedInError) {
      console.error("You've been logged out error", e);
      throw new UntraceableError(
        "You've been logged out. Please sign out and log back in.",
        'none',
        {
          cause: e
        }
      );
    }
    // RefreshUnavailableError (the sign-in service 5xx'd / is unreachable) says nothing about the
    // session, so it must not read as a logout. Surface it as-is; the caller's error handling runs.
    throw e;
  }
};

// 403 is deliberately excluded. Elsewhere in the app, 403 is an established, expected signal for
// "this session is fine, but you don't have permission for this specific record" — see the
// RestrictedCard branch in Dashboard/cards/ChartCard.tsx, DashboardCard, DashboardView,
// useChartEditGate, and SingleChartView's fan-out-forbidden handling. Treating a per-record 403 as a
// session failure here would hijack that UI and bounce the user to login every time they view a
// dashboard containing a chart they're merely not cleared for. (The /me boot check in redux/user.ts
// handles its own 403, where it means the identity has no access at all.) 401 is the authorizer's
// "your token was rejected", including an expired token. 500 is a server error (the authorizer
// answers 401 for token problems), which a plain resend of an idempotent request can ride out.
const isRecoverableSessionStatus = (status: number): boolean => status === 401 || status === 500;

const isIdempotentMethod = (method: string): boolean =>
  ['GET', 'HEAD'].includes(method.toUpperCase());

// A 401 means the API Gateway authorizer rejected the request before it ever reached the lambda
// handler (see bpm-lambdas' jobs/authorize.js) — nothing happened server-side, so resending is safe
// regardless of method. A 500, by contrast, can come from the handler itself after it already did
// real work (e.g. wrote a record, then failed on a downstream step) — blindly resending a mutating
// method in that case risks a duplicate write/side-effect. Only resend a 500 for a naturally
// idempotent method (GET/HEAD); a 500 on a POST/PUT/DELETE/PATCH just surfaces as before this fix.
const isSafeToRetryStatus = (status: number, method: string): boolean =>
  status === 401 || (status === 500 && isIdempotentMethod(method));

export const BpmLambdaFetch = async <T>(
  service: string,
  useDockerLambdas = false,
  useLocalPython = false,
  path = '/',
  passedOptions: Partial<LambdaFetchOptions<T>> = {},
  useGlobalHost = false
): Promise<Response> => {
  const options: LambdaFetchOptions<T> = {
    method: 'GET',
    authenticate: true,
    retries: DEFAULT_RETRIES,
    ...passedOptions,
    headers: { ...passedOptions.headers }
  };

  const headers = {
    ...(R.prop('headers', options) || {})
  };

  // Captured before the corresponding `delete options.*` calls below, so the recoverable-auth-
  // error handling near the bottom of this function knows whether this call carries a session
  // token to refresh, and whether the caller opted out of that handling (see skipAuthRecovery).
  const isAuthenticatedCall = Boolean(options.authenticate);
  const skipAuthRecovery = Boolean(options.skipAuthRecovery);
  // A caller passing retries: 0 (several mutating call sites do — see CroOnboard.tsx, DagStart.tsx,
  // companies-utils.ts) is asserting "never resend this request automatically," precisely because
  // it isn't safe to fire twice. The recovery retry below is a second, independent resend
  // mechanism from the network-level retry loop that retries option governs, so it must honor the
  // same opt-out explicitly rather than only checking HTTP method — isIdempotentMethod alone
  // can't tell a genuinely idempotent GET from a mutating one (CroOnboard's `/add` performs a
  // real AWS onboarding action over GET-style params, with no request body to distinguish it).
  const explicitlyNoRetries = options.retries === 0;
  const visibilityWaitTimeoutMs =
    options.visibilityWaitTimeoutMs ?? DEFAULT_VISIBILITY_WAIT_TIMEOUT_MS;

  if (options.authenticate) {
    headers.Authorization = await buildAuthHeader();
  }
  options.headers = headers;
  delete options.authenticate;
  delete options.skipAuthRecovery;
  delete options.visibilityWaitTimeoutMs;

  let paramStr = '';

  if (options.params) {
    paramStr = '?';
    const keys = Object.keys(options.params);
    for (const key of keys) {
      // If we pass anything - false, null, 0 - APIGateway / Lambda will always interpret it as truthy smh. Only do nils
      // or false, though, since an empty string or a 0 might actually be valid.
      if (R.isNil(options.params[key]) || options.params[key] === false) {
        continue;
      }
      if (paramStr !== '?') {
        paramStr += '&';
      }

      // if we are sending multiple QS-params of the same name, it's passed in as an array
      if (Array.isArray(options.params[key])) {
        for (const val of options.params[key]) {
          if (paramStr !== '?') {
            paramStr += '&';
          }

          paramStr = `${paramStr}${encodeURIComponent(key)}=${encodeURIComponent(val)}`;
        }
      } else {
        paramStr = `${paramStr}${encodeURIComponent(key)}=${encodeURIComponent(
          options.params[key] as ValidParam
        )}`;
      }
    }
  }
  let body: string | undefined;
  if (options.body) {
    if (typeof options.body === 'object') {
      body = JSON.stringify(options.body);
    } else {
      body = String(options.body);
    }
  }

  let devCleanup: (() => void) | undefined;
  if (import.meta.env.DEV && !options.devAllowDuplicate) {
    const fp = buildFingerprint(options.method, service, path, options.params, body);
    if (inFlight.has(fp)) {
      const origin = inFlight.get(fp)!;
      // React StrictMode double-invokes effects in dev and can legitimately produce this warning.
      // If this is an intentional concurrent duplicate, pass devAllowDuplicate: true in fetch options.
      console.error(
        `[fetch-utils] Duplicate in-flight request detected: ${options.method.toUpperCase()} ${service}${path}\n\n` +
          `This is typically caused by a state management issue — common culprits:\n` +
          `  • A useEffect firing more than once (missing/incorrect dep array, or consider migrating to useQuery)\n` +
          `  • A React Query key that doesn't encode all its inputs, causing redundant fetches\n` +
          `  • A component mounting/unmounting in a loop\n` +
          `  • React StrictMode intentionally double-invoking effects (dev-only, expected)\n\n` +
          `To silence this for a known-intentional call, pass devAllowDuplicate: true in the fetch options.\n\n` +
          `Original call site:\n${origin.stack}\n\n` +
          `Duplicate call site (current):\n${new Error('Duplicate request origin').stack}`
      );
    } else {
      inFlight.set(fp, new Error('Original request origin'));
      devCleanup = () => inFlight.delete(fp);
    }
  }

  const initTries = options.retries ?? DEFAULT_RETRIES;
  let tries = initTries + 1;
  delete options.retries;
  let res: Response | undefined;

  // On a deployed build, always route through the docker/lambda host. The
  // local-Python override only applies when the app itself is running on
  // localhost, so a stray VITE_LOCAL_PYTHON_HOST in process.env can never
  // redirect prod traffic to localhost.
  const isLocalDev = window.location.hostname === 'localhost';
  const resolvedHost = isLocalDev
    ? (useLocalPython && REACT_APP_LOCAL_PYTHON_HOST) || REACT_APP_LAMBDA_HOST || ''
    : (useDockerLambdas && REACT_APP_DOCKER_LAMBDA_HOST) || REACT_APP_LAMBDA_HOST || '';

  // Services verified safe in every prod region (eu-west-2 included) route through the all-regions
  // api-global domain instead of the US-only api-prod domain. The two are sibling hostnames, so we
  // swap api-prod -> api-global on the resolved host. In staging/dev the host isn't api-prod, so this
  // is a no-op and the service falls back to its normal host (api-global exists in prod only).
  const baseUrl = useGlobalHost ? resolvedHost.replace('api-prod.', 'api-global.') : resolvedHost;

  const url = `${baseUrl}/${service}${path}${paramStr}`;
  // Route + method only for telemetry — never the query string (avoids PII and keeps cardinality low).
  const endpoint = `${service}${path}`;
  // Fail before the request when the URL is too long.
  if (url.length > MAX_URL_LENGTH) {
    devCleanup?.();
    Mixpanel.track(MxE.LambdaFetchFailed, {
      service,
      endpoint,
      method: options.method,
      error_type: 'url_too_long',
      error_message: URL_TOO_LONG_MESSAGE,
      status: 414,
      ...getFailureContext()
    });
    throw new FetchError(URL_TOO_LONG_MESSAGE, 414);
  }
  let lastError: unknown;
  // Whether the tab was hidden during any failed attempt. Gates the bonus attempt below (see
  // that block for why a live visibility check there isn't enough) and is also surfaced in the
  // give-up telemetry so the visibility-throttling hypothesis can be measured against real
  // traffic, not just inferred from the visibility snapshot at the moment of final failure.
  let pausedForVisibility = false;

  let attemptNumber = 0;
  do {
    attemptNumber += 1;
    try {
      res = await fetch(url, {
        ...options,
        body
      });
      break;
    } catch (e) {
      lastError = e;
      console.error(`Failed to fetch. Error: ${getErrorMessage(e)}`);
      console.error('Tries: ', --tries);
      if (isTabHidden()) {
        pausedForVisibility = true;
      }
      // No point waiting after the last scheduled attempt — nothing is about to retry on this
      // schedule, so it would just delay reaching the bonus-attempt/give-up decision below for no
      // benefit.
      if (tries > 0) {
        await artificialWait(jitteredRetryBackoffMs(attemptNumber));
      }
    }
  } while (tries > 0);

  // Every attempt above still fires on its normal cadence regardless of visibility, so a page
  // that's meant to keep loading/polling in the background keeps doing so. But if the tab was
  // hidden at any point during those attempts, background-tab throttling was almost certainly a
  // factor (see lambda_fetch_failed telemetry). Give it one more try — bounded by
  // visibilityWaitTimeoutMs above (DEFAULT_VISIBILITY_WAIT_TIMEOUT_MS unless the caller opted into
  // the shorter sequential bound) — once the tab is genuinely back in the foreground (or that
  // timeout elapses) before surfacing a failure nobody has even seen yet, rather than leaving a
  // stale error for whenever the user returns — especially since nothing else retries on our
  // behalf (refetchOnWindowFocus is off app-wide).
  //
  // Gated on pausedForVisibility (was the tab hidden at some point) rather than a live
  // isTabHidden() check here: a live check would miss the case where the user already returned
  // during one of the retry loop's earlier backoff waits above, denying the bonus attempt exactly
  // when the user has genuinely come back — the one case this whole mechanism exists for.
  const bonusAttempt = !res && pausedForVisibility && !explicitlyNoRetries;
  if (bonusAttempt) {
    await waitForVisible(visibilityWaitTimeoutMs);
    // The Authorization header above was captured once, before any of this waiting — refresh it
    // so an arbitrarily long wait can't turn a should-have-recovered request into an avoidable
    // 401 from a rotated/expired token.
    if (headers.Authorization) {
      headers.Authorization = await buildAuthHeader();
    }
    try {
      res = await fetch(url, { ...options, body });
    } catch (e) {
      lastError = e;
    }
  }

  if (res) {
    markHostReachable(baseUrl);
  }

  try {
    if (res) {
      // Whether the recoverable-auth-error handling below (DEV-24818) will actually get a chance
      // to run for this response. Used to defer this 500's own failure telemetry until we know
      // whether that handling silently healed it — otherwise every transparently-recovered token-
      // expiry 500 would still record as a real, user-visible failure (misleading on-call/alerting
      // built on this event). 401 was never tracked here (see the condition below) so it needs no
      // equivalent deferral; 429/408/other 5xx never enter the recovery path, so they still track
      // immediately as before.
      const mayAutoRecover =
        isAuthenticatedCall &&
        !skipAuthRecovery &&
        !explicitlyNoRetries &&
        isRecoverableSessionStatus(res.status) &&
        isSafeToRetryStatus(res.status, options.method);

      // Record server/transient HTTP failures (5xx, 429, 408) so the visible-status side of the
      // distribution shows up next to the transport-level give-ups below. awaitStatus throws on non-2xx.
      if (
        !res.ok &&
        (res.status === 429 || res.status === 408 || (res.status >= 500 && !mayAutoRecover))
      ) {
        Mixpanel.track(MxE.LambdaFetchFailed, {
          service,
          endpoint,
          method: options.method,
          error_type: 'http_status',
          status: res.status,
          ...getFailureContext()
        });
      }
      // Only a 500 can reach here with its own failure telemetry still un-recorded (see
      // mayAutoRecover above) — a 401 was never tracked by the block above in the first place.
      const trackDeferredFailure = (status: number) => {
        if (status !== 500) return;
        Mixpanel.track(MxE.LambdaFetchFailed, {
          service,
          endpoint,
          method: options.method,
          error_type: 'http_status',
          status,
          ...getFailureContext()
        });
      };

      try {
        await awaitStatus(res);
        return res;
      } catch (e) {
        // A mid-session token expiry/rejection on any authenticated call (other than the
        // useIsAuthed /me boot check, which owns its own recovery via skipAuthRecovery, or a call
        // that explicitly opted out of automatic retries via retries: 0 — several mutating call
        // sites do, precisely because resending isn't safe) recovers the way useIsAuthed does for
        // /me: on a 401, one forced token refresh (getTokens({forceRefresh: true})) + one retry,
        // falling back to a clean redirect to login rather than a bare "Unauthorized". A 500 on an
        // idempotent request gets the one retry without a refresh.
        if (
          isAuthenticatedCall &&
          !skipAuthRecovery &&
          !explicitlyNoRetries &&
          e instanceof FetchError &&
          isRecoverableSessionStatus(e.status) &&
          isSafeToRetryStatus(e.status, options.method)
        ) {
          const redirectToLogin = () => {
            captureIntendedDestination();
            loginRedirect();
          };

          // A 500 says nothing about the token (the authorizer answers 401 for those), so an
          // idempotent request is resent as-is; only a 401 earns a forced refresh.
          if (e.status === 401) {
            try {
              const { accessToken } = await getTokens({ forceRefresh: true });
              headers.Authorization = `bearer ${accessToken}`;
            } catch (refreshError) {
              trackDeferredFailure(e.status);
              if (refreshError instanceof NotSignedInError) {
                // A 401 from /auth/refresh: the only definitive "the session is gone" answer,
                // unlike a retry that reaches the backend and comes back with its own,
                // session-independent error (handled below).
                console.error(
                  'Token refresh says the session is gone; treating it as unrecoverable',
                  refreshError
                );
                redirectToLogin();
              } else {
                // The refresh endpoint couldn't answer (5xx, network, malformed body): the session
                // may be perfectly fine, so never redirect (a location.replace here fed a reload
                // loop during partial /auth/refresh outages). Surface the original fetch error so
                // the caller's own error handling runs.
                console.error('Token refresh unavailable; not retrying the request', refreshError);
              }
              throw e;
            }
          }

          try {
            const retryRes = await fetch(url, { ...options, body });
            await awaitStatus(retryRes);
            return retryRes;
          } catch (retryError) {
            // Deliberately narrower than isRecoverableSessionStatus (which also allows 500). By the
            // retry leg a 401 means the session is still being rejected, but a repeating 500 is a
            // genuine backend error: redirecting to login would lose page state for it, so surface
            // it like any other session-independent failure instead.
            const retryStillLooksLikeADeadSession =
              retryError instanceof FetchError && retryError.status === 401;

            // Whichever leg actually carries a 500 gets recorded (trackDeferredFailure no-ops for
            // anything else) — preferring the retry's own status since it's the more current proof
            // of what actually happened, falling back to the original only if the retry itself
            // wasn't the 500 (e.g. the original was a deferred 500 and the retry came back 401 or
            // a session-independent 403/404/422 instead). This can never double-count: at most one
            // of the two statuses is ever 500 in the cases that reach here — a genuine 500 on both
            // legs is exactly the "persisting, unrelated backend bug" case above, and is recorded
            // once via the retry's own status.
            const recordableStatus =
              retryError instanceof FetchError && retryError.status === 500
                ? retryError.status
                : e.status;
            trackDeferredFailure(recordableStatus);

            if (retryStillLooksLikeADeadSession) {
              console.error('Recoverable auth error survived retry', retryError);
              redirectToLogin();
            }
            // Otherwise the retry succeeded auth-wise (or there was nothing to refresh) but
            // failed for a session-independent reason instead (a legitimate per-record 403, a
            // 404, a validation 422, a persisting-but-unrelated 500, etc). Surface THAT error
            // as-is either way — forcing a login redirect above already handled the one status
            // (401) that unambiguously still means the session itself is dead; anything else
            // would hijack whatever restricted/empty-state UI the caller renders for it (see
            // isRecoverableSessionStatus's comment above).
            throw retryError;
          }
        }
        throw e;
      }
    }
    console.error(
      `Could not complete request after ${initTries} retries. Request: ${options.method} ${url}.`
    );
    // Transport-level give-up: every fetch() attempt rejected (network loss, CORS failure incl.
    // CORS-less 5xx/504, or abort), so there is no status and no backend message to show.
    const errorType = classifyFetchError(lastError);
    Mixpanel.track(MxE.LambdaFetchFailed, {
      service,
      endpoint,
      method: options.method,
      error_type: errorType,
      error_message: getErrorMessage(lastError),
      attempts: initTries + 1 + (bonusAttempt ? 1 : 0),
      paused_for_visibility: pausedForVisibility,
      ...getFailureContext()
    });
    throw new UntraceableError(
      TRANSPORT_ERROR_MESSAGES[errorType] ?? TRANSPORT_ERROR_MESSAGES.other,
      'network',
      { cause: lastError, host: baseUrl }
    );
  } finally {
    devCleanup?.();
  }
};

// Presigned S3/CDN responses are not backend errors: a non-2xx is a gateway failure and a rejected
// fetch is a network failure, so neither gets a derived FE- id implying an app bug.
export const assertPresignedOk = (res: Response, what: string): void => {
  if (!res.ok) {
    throw new UntraceableError(`${what} (${res.status}).`, 'gateway');
  }
};

export const fetchPresigned = async (
  url: string,
  what: string,
  init?: RequestInit
): Promise<Response> => {
  let res: Response;
  try {
    res = await fetch(url, init);
  } catch (e) {
    throw new UntraceableError(TRANSPORT_ERROR_MESSAGES.network, 'network', { cause: e });
  }
  assertPresignedOk(res, what);
  return res;
};

export const GetS3SignedUrl = async (path: string): Promise<string> => {
  let res = await AdminLambdaFetch(`/signedUrl?path=${path}`);
  res = await awaitJSON(res);
  return R.prop('url', res);
};

// Follows url redirect through JSON payload `url` property
export const S3SignedUrlFetch = async (path: string, options: object = {}): Promise<any> => {
  const signedURL = await GetS3SignedUrl(path);
  const res = await fetch(signedURL, options);
  return res;
};

export const GetS3SignedUrl2 = async (
  bucket: string,
  key: string,
  contentType?: string
): Promise<string> => {
  const params: Record<string, string> = {
    bucket,
    key
  };

  if (contentType) {
    params.contentType = contentType;
  }
  let res = await ToolsLambdaFetch('/s3_signed_url', {
    params
  });
  res = await awaitJSON(res);
  return R.prop('url', res);
};

export const S3SignedUrlFetch2 = async (
  bucket: string,
  key: string,
  contentType?: string
): Promise<Response> => {
  const signedURL = await GetS3SignedUrl2(bucket, key, contentType);
  const res = await fetch(signedURL);
  return res;
};

// papaparse ships untyped; declare the slice of ParseConfig we accept.
interface CsvParseConfig {
  header?: boolean;
  skipEmptyLines?: boolean | 'greedy';
  transformHeader?: (header: string, index: number) => string;
  transform?: (value: string, field: string | number) => unknown;
}

// Downloads a `<bucket>/<key>` cache CSV (as returned by the lambdas'
// uploadCsvToCache) and parses it. Defaults to header rows with empty lines
// skipped; pass parseConfig to override or extend (e.g. transformHeader).
export const fetchAndParseCachedCsv = async <T>(
  cacheKey: string,
  parseConfig: CsvParseConfig = {}
): Promise<T[]> => {
  const res = await S3SignedUrlFetch(cacheKey);
  if (!res.ok) {
    throw new UntraceableError(
      `Failed to download cached CSV ${cacheKey} (${res.status}).`,
      'gateway'
    );
  }
  const text = await res.text();
  const { data } = Papa.parse<T>(text, {
    header: true,
    skipEmptyLines: true,
    ...parseConfig
  });
  return data || [];
};

type PotentialUnfoundCheckMaker<T> = () => Promise<T | false>;

export const makePotentiallyUnfoundCheck = async (
  fetcher: () => Promise<Response>
): Promise<Response | false> => {
  try {
    const res = await fetcher();
    if (res.status === 200) {
      return res;
    }
    if (res.status !== 404) {
      let errorText: string;
      try {
        errorText = await awaitText(res);
      } catch {
        errorText = res.statusText;
      }
      throw new Error(errorText);
    }
    return false;
  } catch {
    return false;
  }
};

type ErrorCheckMaker = PotentialUnfoundCheckMaker<string>;

export const makePollingErrorChecker = async (
  fetcher: () => Promise<Response>
): Promise<string | false> => {
  const res = await makePotentiallyUnfoundCheck(fetcher);
  if (res !== false) {
    return res.text();
  }
  return false;
};

interface PollOptions<T> {
  fetchMaker: PotentialUnfoundCheckMaker<T>;
  errorCheckMaker?: ErrorCheckMaker;
  period?: number; // Milliseconds
  timeout?: number; // Seconds. If -1, then no timeout
  maxAttempts?: number; // Maximum tries to fetch the resource
}

export const poll = async <T>({
  fetchMaker,
  errorCheckMaker,
  period = S3_POLLING_PERIOD,
  timeout = S3_POLLING_TIMEOUT,
  maxAttempts
}: PollOptions<T>): Promise<T> => {
  const start = Date.now();
  let latest = Date.now();
  let attemptNumber = 0;
  while (timeout < 0 || (latest - start) / 1000 < timeout) {
    if (attemptNumber === maxAttempts) {
      throw new UntraceableError(
        `Attempted to fetch the maximum number of times: ${maxAttempts}`,
        'gateway'
      );
    }
    await artificialWait(period);
    try {
      const response = await fetchMaker();
      if (response !== false) {
        return response;
      }

      if (errorCheckMaker) {
        const errorMessage = await errorCheckMaker();
        if (errorMessage !== false) {
          throw new UntraceableError(errorMessage, 'job');
        }
      }
    } catch (e) {
      // Rethrow typed errors unchanged so a FetchError keeps its requestId.
      if (e instanceof SyntaxError) {
        throw new UntraceableError(e.message, 'gateway', { cause: e });
      }
      throw e;
    }
    latest = Date.now();
    attemptNumber++;
  }
  throw new UntraceableError(
    'Stopped waiting for this job to finish. It may still be running or partly applied; check the current state before sending again.',
    'timeout'
  );
};

interface PollS3Options {
  bucket: string;
  filename: string;
  mimeType: string;
  ext?: string;
  autoDownload?: boolean;
  overloadFilename?: string;
  noTimeout?: boolean;
  withErrors?: boolean;
  timeout?: number;
  maxAttempts?: number;
  period?: number;
}

export const pollS3 = async ({
  bucket,
  filename,
  mimeType,
  ext,
  autoDownload = true,
  maxAttempts = -1, // Default no maximum
  overloadFilename,
  noTimeout = false,
  withErrors = false,
  timeout = S3_POLLING_TIMEOUT,
  period = S3_POLLING_PERIOD
}: PollS3Options): Promise<Blob> => {
  let errorCheckMaker: undefined | (() => Promise<string | false>);
  if (withErrors) {
    errorCheckMaker = () =>
      makePollingErrorChecker(() =>
        S3SignedUrlFetch2(bucket, `${filename}.error.txt`, 'text/plain')
      );
  }
  const response = await poll({
    fetchMaker: () =>
      makePotentiallyUnfoundCheck(() =>
        S3SignedUrlFetch2(bucket, ext ? `${filename}.${ext}` : filename, mimeType)
      ),
    errorCheckMaker,
    period,
    timeout: noTimeout ? -1 : timeout,
    maxAttempts
  });
  if (!response) {
    throw new UntraceableError(
      `Unable to download s3://${bucket}/${filename}${ext || ''}`,
      'gateway'
    );
  }
  const content = await response.blob();
  if (autoDownload) {
    download(content, overloadFilename || filename, mimeType);
  }
  return content;
};

// Shared by LinearOptimizations and StreamingOptimizations' Run View export: converts a run's CSV
// rows to an Excel file via the generic Java csv_to_excel lambda, then downloads the result from
// S3. Lives here (not download-utils.ts) since it needs JavaLambdaFetch/awaitJSON/pollS3, and
// download-utils.ts already imports `download` from this file -- adding the reverse import would
// be circular.
export const exportOptimizationRunSummaryToExcel = async <T>(
  rows: T[],
  company: string,
  runId: string
): Promise<void> => {
  const result = await JavaLambdaFetch('/csv_to_excel', {
    method: 'POST',
    body: { csv: Papa.unparse(rows) }
  });
  const { s3Out } = await awaitJSON<{ s3Out: string }>(result);
  await pollS3({
    bucket: 'bpm-cache',
    mimeType: 'application/vnd.ms-excel',
    filename: s3Out,
    overloadFilename: `Optimization_${company}_${runId}.xlsx`
  });
};

export const COMET_LOADING_BUCKET = 'bpm-comet-loading';

// Interim progress payload a comet lambda writes to {uuid}.txt while a job runs (a sibling of
// cometErrorMessage). Shape mirrors bpm-python-lambdas/utils/cometProgressUtils.py: the running
// counts live in `message` and the lambda owns `percent`. The frontend keeps polling while a
// cometProgress payload is present and not done.
export interface CometProgress {
  percent?: number;
  message?: string;
  done?: boolean;
}

interface PollCometResultOptions {
  ext?: string;
  period?: number;
  timeout?: number;
  noTimeout?: boolean;
  onProgress?: (progress: CometProgress) => void;
}

// Only the two terminal poll outcomes are reported: the worker's own cometErrorMessage and a real
// timeout. A not-yet-written result file (404) is just "still waiting" and is deliberately never
// reported, so a healthy poll loop produces no telemetry. reportError throttles repeats.
const reportCometPollFailure = (e: unknown, uuid: string, ext: string): void => {
  if (!(e instanceof UntraceableError)) {
    return;
  }
  if (e.kind === 'job') {
    reportError(e, { operation: 'comet_job_error', fingerprint: uuid, extra: { uuid, ext } });
  } else if (e.kind === 'timeout') {
    reportError(e, { operation: 'comet_poll_timeout', fingerprint: uuid, extra: { uuid, ext } });
  }
};

// Thrown when a comet trigger gets something other than {comet:true, uuid} back: the route's lambda
// isn't wrapped with cometInputWrapper (typically the frontend shipped before the bpm-lambdas
// deploy). Without this guard the caller polled `undefined.json` forever (Segmentation Mapping
// outage, 2026-10-15).
export const describeMissingCometAck = (method: string, path: string, text: string): string =>
  `Comet trigger to ${method} ${path} did not return a comet ack ({comet:true, uuid}). The lambda ` +
  'is probably not wrapped with cometInputWrapper, or that bpm-lambdas change is not deployed ' +
  `yet. Deploy it before shipping this frontend call. Response (first 200 chars): ${text.slice(0, 200)}`;

// Shared polling tail for Comet invocations: reads `{uuid}.{ext}` from the comet-loading
// bucket and unwraps the cometErrorMessage/cometProgress sentinels a Comet worker writes alongside
// its result (bpm-lambdas' cometOutputWrapper).
const pollCometResult = <K>(
  uuid: string,
  {
    ext = 'txt',
    period = S3_POLLING_PERIOD,
    timeout = COMET_WORKER_TIMEOUT_SECONDS,
    noTimeout = false,
    onProgress
  }: PollCometResultOptions = {}
): Promise<K> =>
  poll<K>({
    period,
    timeout: noTimeout ? COMET_NO_TIMEOUT_CEILING_SECONDS : timeout,
    fetchMaker: async () => {
      const response = await makePotentiallyUnfoundCheck(() =>
        S3SignedUrlFetch2(COMET_LOADING_BUCKET, `${uuid}.${ext}`, 'text/plain')
      );
      if (response === false) {
        return false;
      }
      const parsed = JSON.parse(await response.text());
      if (parsed && typeof parsed === 'object' && 'cometErrorMessage' in parsed) {
        throw new UntraceableError(parsed.cometErrorMessage, 'job');
      }
      const progress: CometProgress | undefined = parsed?.cometProgress;
      if (progress && !progress.done) {
        onProgress?.(progress);
        return false;
      }
      if (progress) {
        // Final payload carries the done:true sentinel — surface it, then strip the framework
        // key so placement-map consumers never see it as a placement entry.
        onProgress?.(progress);
        delete parsed.cometProgress;
      }
      return parsed as K;
    }
  }).catch((e: unknown) => {
    reportCometPollFailure(e, uuid, ext);
    throw e;
  });

// The reserved trigger field bpm-lambdas' cometInputWrapper (lib/cometWrapper.ts, DEV-24668)
// checks for on a handler composed with the Comet self-invoke wrapper pair. Exported for a
// caller that wants to fire a Comet trigger directly without going through fetchCometSelfInvoke
// (e.g. a fire-and-forget call whose result isn't polled for — see kickOffPythonABCDRunner).
export const COMET_TRIGGER_FIELD = '__cometTrigger';

// DEV-24669: calls a handler composed with bpm-lambdas' Comet self-invoke wrapper pair (DEV-24668)
// through its own endpoint/access-control. Sends the trigger field alongside lambdaArgs -- as a
// POST body, or (DEV-24670 incident fix) as GET query params for a route that's semantically a
// read, since bpm-lambdas' cometInputWrapper reads either.
//
// Most callers don't need this function at all: awaitJSON (above) transparently detects a
// {comet:true, uuid} ack and polls for the result on its own -- a caller just adds
// __cometTrigger to its request body/params and keeps calling its normal fetcher + awaitJSON
// exactly as before. This wrapper exists only for a caller that wants non-default polling
// behavior (onProgress, a custom period/timeout) -- it reads the ack directly (bypassing
// awaitJSON's zero-config auto-poll, which would otherwise conflict with these custom options)
// and drives pollCometResult itself.
export interface FetchCometSelfInvokeOptions {
  method?: 'GET' | 'POST'; // default 'POST' -- 'GET' for a route that's semantically a read
  noTimeout?: boolean;
  period?: number;
  timeout?: number;
  onProgress?: (progress: CometProgress) => void;
}

export const fetchCometSelfInvoke = async <T, K>(
  fetcher: BpmLambdaFetcher,
  path: string,
  lambdaArgs: T,
  {
    method = 'POST',
    noTimeout = false,
    period = S3_POLLING_PERIOD,
    timeout = COMET_WORKER_TIMEOUT_SECONDS,
    onProgress
  }: FetchCometSelfInvokeOptions = {}
): Promise<K> => {
  const triggerArgs = { ...lambdaArgs, [COMET_TRIGGER_FIELD]: true };
  const result = await fetcher(
    path,
    method === 'GET'
      ? { method: 'GET', params: triggerArgs }
      : { method: 'POST', body: triggerArgs }
  );
  const text = await result.text();
  let ack: unknown;
  try {
    ack = JSON.parse(text);
  } catch {
    ack = undefined;
  }
  if (!isCometAckResponse(ack)) {
    const err = new UntraceableError(describeMissingCometAck(method, path, text), 'gateway');
    reportError(err, {
      operation: 'comet_missing_ack',
      fingerprint: path,
      extra: { path, method, status: result.status }
    });
    throw err;
  }
  return pollCometResult<K>(ack.uuid, { ext: 'json', period, timeout, noTimeout, onProgress });
};

export type BpmLambdaFetcher = <T>(
  path: string,
  fetchOptions?: Partial<LambdaFetchOptions<T>>
) => Promise<Response>;

interface CometFallbackParams<T> {
  service: BpmLambdaFetcher;
  standardEndpoint: string;
  input: T;
  method?: LambdaFetchOptions<T>['method'];
  period?: number;
  noTimeout?: boolean;
  timeoutMs?: number;
  ttlMs?: number;
}

const COMET_FALLBACK_LS_PREFIX = 'comet_fallback_v1';
const DEFAULT_COMET_TIMEOUT_MS = 25_000;
const DEFAULT_COMET_TTL_MS = 7 * 24 * 60 * 60 * 1000;
const COMET_FALLBACK_TIMEOUT_MESSAGE = 'comet_fallback_timeout';

// Errors comet actually fixes: the direct call timed out, or its response blew
// past the API Gateway / Lambda payload limit. Anything else (auth, bad request,
// a genuine 500) is surfaced as-is — retrying through comet wouldn't help.
const COMET_RETRIABLE_STATUSES = new Set([413, 502, 504]);

const isCometRetriableError = (err: unknown): boolean =>
  (err instanceof Error && err.message === COMET_FALLBACK_TIMEOUT_MESSAGE) ||
  (err instanceof FetchError && COMET_RETRIABLE_STATUSES.has(err.status));

const buildCometFallbackKey = <T>(endpoint: string, input: T): string => {
  const stable = JSON.stringify(
    Object.fromEntries(
      Object.entries(input as Record<string, unknown>).sort(([a], [b]) => a.localeCompare(b))
    )
  );
  return `${COMET_FALLBACK_LS_PREFIX}:${endpoint}:${stable}`;
};

const isCometFallbackMarked = (key: string): boolean => {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) {
      return false;
    }
    const expiry = parseInt(raw, 10);
    if (Number.isNaN(expiry) || Date.now() > expiry) {
      localStorage.removeItem(key);
      return false;
    }
    return true;
  } catch {
    return false;
  }
};

const markCometFallback = (key: string, ttlMs: number): void => {
  try {
    localStorage.setItem(key, String(Date.now() + ttlMs));
  } catch {
    // localStorage unavailable or quota exceeded — fallback still works, it just
    // won't be remembered for next time.
  }
};

/*
 * Try the direct Lambda first; if it times out or trips the payload limit, fall
 * back to the endpoint's Comet self-invoke route (S3-backed, no gateway timeout or response-size cap).
 *
 * Error contract (the reason this wrapper exists):
 *   - Direct call succeeds                -> return data, surface nothing.
 *   - Direct call hits a comet-retriable  -> fall back to comet SILENTLY. The
 *     error (timeout/413/502/504)            timeout/payload error is consumed; we
 *                                            don't announce that we retried.
 *   - Comet fallback itself fails         -> the error propagates to the caller so
 *                                            the UI can surface it. This is the only
 *                                            way a timeout/payload error reaches the
 *                                            user — once comet couldn't save it.
 *   - Direct call hits any other error    -> rethrow immediately (comet wouldn't
 *                                            fix it), so the caller surfaces it.
 *
 * Once a retriable error is seen for a given (lambda, input), a localStorage marker
 * routes future calls straight to comet for ttlMs, skipping the wasted timeout.
 */
export const fetchWithCometFallback = async <T, K>({
  service,
  standardEndpoint,
  input,
  method = 'get',
  period = S3_POLLING_PERIOD,
  noTimeout = false,
  timeoutMs = DEFAULT_COMET_TIMEOUT_MS,
  ttlMs = DEFAULT_COMET_TTL_MS
}: CometFallbackParams<T>): Promise<K> => {
  const fetchOpts: Partial<LambdaFetchOptions<T>> = { method };
  if (method === 'get' || method === 'GET') {
    fetchOpts.params = input;
  } else {
    fetchOpts.body = input;
  }

  // The fallback is the endpoint's own Comet self-invoke route.
  const runFallback = (): Promise<K> =>
    fetchCometSelfInvoke<T, K>(service, standardEndpoint, input, {
      method: method.toUpperCase() === 'GET' ? 'GET' : 'POST',
      noTimeout,
      period
    });

  const lsKey = buildCometFallbackKey(standardEndpoint, input);
  if (isCometFallbackMarked(lsKey)) {
    return runFallback();
  }

  const timeoutRace = new Promise<never>((_, reject) => {
    setTimeout(() => reject(new Error(COMET_FALLBACK_TIMEOUT_MESSAGE)), timeoutMs);
  });

  try {
    return await Promise.race([
      service<T>(standardEndpoint, fetchOpts).then(res => awaitJSON<K>(res)),
      timeoutRace
    ]);
  } catch (err) {
    if (!isCometRetriableError(err)) {
      throw err;
    }
    markCometFallback(lsKey, ttlMs);
    return runFallback();
  }
};

interface S3PutOptions {
  bufferEncoding?: string;
  contentEncoding?: string;
  contentType?: string;
}
// TODO: if I wasn't lazy, I'd make a type for what I know this lambda returns
export const S3Put = (
  bucket: string,
  key: string,
  body: string,
  options?: S3PutOptions
): Promise<any> =>
  ToolsLambdaFetch('/s3', {
    method: 'post',
    body: {
      body,
      bucket,
      key,
      ...options
    }
  });

export const S3PutPng = (bucket: string, key: string, body: string): Promise<any> =>
  S3Put(bucket, key, body.replace(/^data:image\/png;base64,/, ''), {
    bufferEncoding: 'base64',
    contentType: 'image/png'
  });

export const S3PutMp4 = (bucket: string, key: string, body: string): Promise<any> =>
  S3Put(bucket, key, body.replace(/^data:video\/mp4;base64,/, ''), {
    bufferEncoding: 'base64',
    contentType: 'video/mp4'
  });

export const S3Delete = (bucket: string, key: string): Promise<any> =>
  ToolsLambdaFetch('/s3', {
    method: 'delete',
    params: {
      bucket,
      key
    }
  });

interface S3ListParams {
  bucket: string;
  prefix: string;
  maxKeys?: number;
  contains?: string;
}

export const S3List = async (params: S3ListParams): Promise<string[]> => {
  const res = await ToolsLambdaFetch('/S3List', { params });
  const data = await awaitJSON<{ keys: string[] }>(res);
  return data.keys ?? [];
};

const makeFetcher =
  (service: string, useDockerLambdas = false, useLocalPython = false, useGlobalHost = false) =>
  <T>(path: string, fetchOptions?: Partial<LambdaFetchOptions<T>>): Promise<Response> =>
    BpmLambdaFetch(service, useDockerLambdas, useLocalPython, path, fetchOptions, useGlobalHost);

export const ABCDLambdaFetch = makeFetcher('abcd', true);
export const ABCDPyLambdaFetch = makeFetcher('abcdpy', true, true);
export const AdminLambdaFetch = makeFetcher('admin', true);
export const AdsMonitorLambdaFetch = makeFetcher('adsmonitor', true);
export const AlexaVisibilityLambdaFetch = makeFetcher('alexavisibility', true);
export const AmazonLambdaFetch = makeFetcher('amazon', true);
export const AmcLambdaFetch = makeFetcher('amc', true);
export const AnomalyDetectionLambdaFetch = makeFetcher('anomalydetection', true);
export const ApiSentryLambdaFetch = makeFetcher('apisentry', true);
export const BenchmarkingLambdaFetch = makeFetcher('benchmarking', true);
export const BillingAutomationLambdaFetch = makeFetcher('billingautomation', true);
export const ClientLambdaFetch = makeFetcher('client');
export const ClientToolsLambdaFetch = makeFetcher('clienttools');
export const CopyTigerLambdaFetch = makeFetcher('copytiger', true);
export const CopyTigerPyLambdaFetch = makeFetcher('copytigerpy', true, true);
export const CommerceOpsLambdaFetch = makeFetcher('commerceops', true);
export const CreativeLambdaFetch = makeFetcher('creative', true);
// The Python-backed scenario-planning direct routes (bpm-python-lambdas' scenarioplanning-py app,
// base path "scenarioplanningpy"). DEV ticket: Comet self-invoke pilot for
// getBaselineGeoMMMScenario.
export const ScenarioPlanningPyLambdaFetch = makeFetcher('scenarioplanningpy', true, true);
export const CroOnboardLambdaFetch = makeFetcher('croonboard', true);
export const CrossChannelLambdaFetch = makeFetcher('crosschannel', true);
export const DagLambdaFetch = makeFetcher('dag', true);
export const DigitalAuditLambdaFetch = makeFetcher('digitalaudit', true);
export const DashboardingLambdaFetch = makeFetcher('dashboarding', true);
// filters is eu-west-2-safe (Postgres-only) → route it through api-global (all regions incl. eu-west-2).
export const FiltersLambdaFetch = makeFetcher('filters', true, false, true);
export const FlashtalkingLambdaFetch = makeFetcher('flashtalking', true);
export const HROASLambdaFetch = makeFetcher('hroas', true, false, true);
export const HROASPyLambdaFetch = makeFetcher('hroaspy', true, true);
export const JavaLambdaFetch = makeFetcher('java', true);
export const JobsLambdaFetch = makeFetcher('jobs', true);
export const LinearBuyingLambdaFetch = makeFetcher('linearbuying', true, false, true);
export const LinearLambdaFetch = makeFetcher('linear', true);
export const LinearOptimizationsLambdaFetch = makeFetcher('linearoptimizations', true, false, true);
export const LinearPerformanceLambdaFetch = makeFetcher('linearperformance', true);
export const MetaLambdaFetch = makeFetcher('meta', true);
export const MetricsLambdaFetch = makeFetcher('metrics', true);
export const MiscLambdaFetch = makeFetcher('misc', true);
export const MobiusSlidesLambdaFetch = makeFetcher('mobiusslides', true);
export const NexxenLambdaFetch = makeFetcher('nexxen', true);
export const OracleLambdaFetch = makeFetcher('oracle', true);
export const SegmentationMappingLambdaFetch = makeFetcher('segmentationmapping', true);
export const SheetsLambdaFetch = makeFetcher('sheets', true, false, true);
export const SingleChannelLambdaFetch = makeFetcher('singlechannel', true);
export const SiteMonitoringLambdaFetch = makeFetcher('sitemonitoring', true);
export const SlidesLambdaFetch = makeFetcher('slides', true);
export const StreamingBuyingLambdaFetch = makeFetcher('streamingbuying');
export const StreamingLambdaFetch = makeFetcher('streaming', true);
export const StreamingOptimizationsLambdaFetch = makeFetcher('streamingoptimizations', true);
export const StreamingOrdersLambdaFetch = makeFetcher('streaming-orders', true);
export const StreamingPerformanceLambdaFetch = makeFetcher('streamingperformance', true);
export const StreamingUtilsLambdaFetch = makeFetcher('streamingutils', true, false, true);
export const StreamingDeliveryLambdaFetch = makeFetcher('streaming-delivery', true);
export const StreamingV2LambdaFetch = makeFetcher('streamingv2', true);
export const StreamingNetworksLambdaFetch = makeFetcher('streaming-networks', true);
export const ToolsLambdaFetch = makeFetcher('tools', true);
export const TvadCampaignAuditLambdaFetch = makeFetcher('tvadcampaignaudits', true);
export const UsersLambdaFetch = makeFetcher('users', true, false, true);
export const WtoLambdaFetch = makeFetcher('wto', true, false, true);
export const YoutubePerformanceLambdaFetch = makeFetcher('youtubeperformance', true);
export const YoutubePlanningLambdaFetch = makeFetcher('youtubeplanning', true);
